<template>
  <div class="property-fire-plan">
    <p class="goal">
      <strong>核心目标：</strong>实现「打早、打小、打了」，在消防队到达前，有效控制或扑灭初起火灾，最大限度保障人员生命财产安全。
    </p>

    <section class="block">
      <h3 class="block-title">第一部分：核心原则与触发条件</h3>

      <h4 class="sub-title">1.1 适用火情</h4>
      <ul class="dot-list">
        <li>单个房间冒烟、起火。</li>
        <li>垃圾桶、杂物、电线等小型明火。</li>
        <li>厨房因用火不慎引发的灶台火灾。</li>
        <li>汽车自燃初期。</li>
        <li>（一句话：在自身安全前提下，能用灭火器或消火栓一盘水带扑灭的火。）</li>
      </ul>

      <h4 class="sub-title">1.2 绝对禁止</h4>
      <ul class="dot-list">
        <li>严禁盲目扑救电气火灾（未断电前）。</li>
        <li>严禁打开可能有爆燃风险的房间门。</li>
        <li>严禁在烟雾大、能见度低时无防护深入。</li>
        <li>严禁在火势已蔓延、超出单个房间范围时冒险。</li>
      </ul>
    </section>

    <section class="block">
      <h3 class="block-title">第二部分：应急组织与职责（最小作战单元）</h3>
      <table class="duty-table">
        <thead>
          <tr>
            <th>岗位</th>
            <th>第一职责</th>
            <th>第二职责</th>
            <th>装备</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>第一发现人（任何员工/业主）</td>
            <td>大声呼救，立即按下最近的手动火灾报警按钮。</td>
            <td>在确保安全前提下，使用最近的灭火器尝试灭火。</td>
            <td>手机（用于报警）、嗓子（用于呼喊）</td>
          </tr>
          <tr>
            <td>消防控制室值班员</td>
            <td>
              1. 确认火警位置，立即通知巡逻岗/灭火组到场。<br>
              2. 启动应急广播，通知人员疏散。<br>
              3. 拨打 119 报警（必须！）。
            </td>
            <td>远程启动相关消防设备（喷淋泵、防排烟机）。</td>
            <td>火灾报警主机、应急广播话筒、电话、对讲机</td>
          </tr>
          <tr>
            <td>巡逻岗/灭火组（1～2 人）</td>
            <td>3 分钟内携带灭火器、对讲机到达现场，判断火情并开展扑救。</td>
            <td>引导人员疏散，向控制室反馈现场情况。</td>
            <td>灭火器、消防斧、强光手电、防烟面罩、对讲机</td>
          </tr>
          <tr>
            <td>支援组/微型消防站（后续 3～4 人）</td>
            <td>穿戴全套装备，连接消火栓水带，建立第二道灭火防线。</td>
            <td>设立警戒线，准备接应消防车，转移重要物资。</td>
            <td>灭火防护服、水带、水枪、扳手、安全绳</td>
          </tr>
          <tr>
            <td>项目经理/总指挥</td>
            <td>赶赴现场或控制室，做出全面决策，调配资源，对外联络。</td>
            <td>评估是否需要启动全面应急预案，指挥人员疏散。</td>
            <td>对讲机、扩音器、项目平面图</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="block">
      <h3 class="block-title">第三部分：处置流程（标准作业程序 - SOP）</h3>

      <h4 class="phase-title">第一阶段：报警与确认（黄金 1 分钟）</h4>
      <p class="step-label"><strong>1.</strong> 任何人员发现火情（烟、味、光），立即执行：</p>
      <ul class="dot-list nested">
        <li>大声呼喊：「X 栋 X 层着火啦！」（或按下手动报警按钮）。</li>
        <li>呼叫支援：用对讲机或电话呼叫监控中心：「监控中心，[具体位置]发现火情，请求灭火组支援！」</li>
        <li>尝试灭火：如果火势很小（如垃圾桶），确保自身安全且熟悉操作，使用最近处的灭火器扑救。</li>
      </ul>
      <p class="step-label"><strong>2.</strong> 消防控制室收到信号（自动或手动）后，立即执行：</p>
      <ul class="dot-list nested">
        <li>查看主机报警点，确认位置。</li>
        <li>通知：「巡逻岗、灭火组，[具体位置]报火警，请立即前往确认！」</li>
        <li>启动该区域应急广播：「各位业主请注意，现在播报紧急通知……」（避免直接喊「火灾」，可以说「紧急情况，请有序撤离」）。</li>
      </ul>

      <h4 class="phase-title">第二阶段：初期扑救（关键 3 分钟）</h4>
      <p class="step-label"><strong>3.</strong> 灭火组（巡逻岗）到达现场后，按「一看二断三灭」程序操作：</p>
      <ul class="dot-list nested">
        <li><strong>一看：</strong>观察火势大小、烟雾颜色、有无人员被困、燃烧物是什么（固体、液体、电气）？手背试门温，门烫手切勿直接打开。</li>
        <li><strong>二断：</strong>如果是电气设备起火，应先通知控制室或工程部切断该区域电源。</li>
        <li>
          <strong>三灭：</strong>
          <ul class="sub-dot-list">
            <li>固体火（A 类）：用水基灭火器或消火栓水。</li>
            <li>液体/气体火（B/C 类）：用干粉或二氧化碳灭火器。</li>
            <li>电气火（E 类，已断电）：用干粉或二氧化碳灭火器。</li>
            <li>油锅火（F 类）：用灭火毯覆盖，或干粉灭火器。</li>
          </ul>
        </li>
      </ul>
      <p class="step-label"><strong>4.</strong> 灭火要领：</p>
      <ul class="dot-list nested">
        <li><strong>站位：</strong>站在上风或侧风方向。</li>
        <li><strong>距离：</strong>距火焰 2～3 米（干粉），1～2 米（二氧化碳）。</li>
        <li><strong>对准：</strong>对准火焰根部左右扫射。</li>
        <li><strong>协同：</strong>一人灭火，一人在后方协助并观察。</li>
      </ul>

      <h4 class="phase-title">第三阶段：扩大响应与控制（5～10 分钟）</h4>
      <p class="step-label"><strong>5.</strong> 灭火组判断：</p>
      <ul class="dot-list nested">
        <li><strong>成功：</strong>火被扑灭，报告控制室，留守现场防止复燃，等待调查。</li>
        <li><strong>失败：</strong>火势未控制或扩大，立即报告：「火势较大，请求启动消火栓，请求支援！」同时，组织该楼层及上一层人员疏散。</li>
      </ul>
      <p class="step-label"><strong>6.</strong> 支援组/微型消防站接报后：</p>
      <ul class="dot-list nested">
        <li>穿戴好防护装备，赶赴现场。</li>
        <li>
          熟练使用墙式消火栓：① 打开箱门，按下启泵按钮；② 铺开水带，连接水枪；③ 另一人连接水带接口与阀门；④ 逆时针打开阀门，握紧水枪对准火点。
        </li>
      </ul>

      <h4 class="phase-title">第四阶段：移交与收尾</h4>
      <p class="step-label"><strong>7.</strong> 消防队到达：灭火组负责人立即向消防指挥员汇报：</p>
      <ul class="dot-list nested">
        <li>火情位置、燃烧物、有无人员被困、已采取的措施、消防设施使用情况。</li>
      </ul>
      <p class="step-label"><strong>8.</strong> 火场警戒与清理：配合消防队设立警戒，保护现场。火灾扑灭后，通风排烟，清点器材，拍照记录，填写《火灾事故报告》。</p>
    </section>

    <section class="block">
      <h3 class="block-title">第四部分：关键检查清单与注意事项</h3>

      <h4 class="sub-title">【扑救前必查】</h4>
      <ul class="check-list">
        <li>[ ] 是否已报警（119/控制室）？</li>
        <li>[ ] 是否清楚燃烧物类型？</li>
        <li>[ ] 所处位置是否为上风方向？</li>
        <li>[ ] 逃生通道是否畅通？</li>
      </ul>

      <h4 class="sub-title">【安全红线】</h4>
      <ul class="dot-list">
        <li>电器着火先断电，油锅着火勿用水。</li>
        <li>浓烟关门待救援，小火快跑莫贪财。</li>
        <li>液化气瓶着火，若阀门完好，应先关阀；若阀门损坏，应冷却瓶体，防止轰然。</li>
      </ul>

      <h4 class="sub-title">【设备使用口诀】</h4>
      <ul class="dot-list">
        <li>灭火器：「提、拔、握、压」（提瓶、拔销、握管、压把）。</li>
        <li>消火栓：「按、接、开、射」（按泵、接带、开阀、射水）。</li>
        <li>消防广播：「清、准、稳」（语音清晰、信息准确、语气稳定）。</li>
      </ul>
    </section>
  </div>
</template>

<script>
export default {
  name: 'PropertyInitialFireEmergencyPlan'
}
</script>

<style scoped>
.property-fire-plan {
  font-size: 14px;
  color: #303133;
  line-height: 1.65;
}

.goal {
  margin: 0 0 20px;
  padding: 14px 16px;
  background: #fef0f0;
  border: 1px solid #fde2e2;
  border-radius: 6px;
  border-left: 4px solid #f56c6c;
}

.block {
  margin-bottom: 28px;
}

.block:last-child {
  margin-bottom: 0;
}

.block-title {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  border-left: 4px solid #199ed7;
  padding-left: 10px;
}

.sub-title {
  margin: 16px 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #606266;
}

.sub-title:first-of-type {
  margin-top: 4px;
}

.phase-title {
  margin: 20px 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #409eff;
}

.phase-title:first-of-type {
  margin-top: 8px;
}

.step-label {
  margin: 12px 0 6px;
}

.dot-list,
.check-list {
  margin: 0 0 8px;
  padding-left: 1.25em;
}

.dot-list li,
.check-list li {
  margin-bottom: 6px;
}

.dot-list.nested {
  margin-top: 4px;
}

.sub-dot-list {
  margin: 6px 0 0;
  padding-left: 1.25em;
}

.sub-dot-list li {
  margin-bottom: 4px;
}

.duty-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}

.duty-table th,
.duty-table td {
  border: 1px solid #dcdfe6;
  padding: 10px 10px;
  vertical-align: top;
  word-break: break-word;
}

.duty-table th {
  background: #f5f7fa;
  font-weight: 600;
  text-align: left;
}

.duty-table td:first-child {
  width: 18%;
  font-weight: 600;
  background: #fafafa;
}
</style>
